import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

export default function MerchantRegister() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    business_name: '',
    contact_person: '',
    email: '',
    phone: '',
    address: '',
    password: '',
    confirmPassword: '',
    oil_brands: '',
    filter_brands: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const payload = {
        business_name: formData.business_name,
        contact_person: formData.contact_person,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        password: formData.password,
        verify_password: formData.confirmPassword,
        oil_brands: formData.oil_brands.split(',').map(b => b.trim()).filter(b => b),
        filter_brands: formData.filter_brands.split(',').map(b => b.trim()).filter(b => b)
      };

      await axios.post(`${API}/merchant/register`, payload);
      toast.success('Registration submitted! Please wait for admin approval.');
      navigate('/merchant/login');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <nav className="border-b border-border bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Link to="/" className="flex items-center space-x-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_vehicle-maintenance-21/artifacts/76wdsgvy_WhatsApp%20Image%202025-12-16%20at%2017.28.15.jpeg" 
                alt="Logo" 
                className="h-12 w-12 object-contain"
              />
              <span className="text-xl font-heading font-bold text-foreground">EXCEL ERA</span>
            </Link>
            <Link to="/merchant/login">
              <Button variant="outline" className="font-bold uppercase text-sm">
                Merchant Login
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white border-2 border-border rounded-sm shadow-hard p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-heading font-bold text-foreground mb-2">Merchant Registration</h1>
            <p className="text-muted-foreground">Register your business to supply oil and filters</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="bg-accent p-4 rounded-sm mb-6">
              <h2 className="font-bold text-foreground mb-3">Business Information</h2>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="business_name" className="font-bold uppercase text-sm tracking-wide">Business Name *</Label>
                  <Input
                    id="business_name"
                    required
                    data-testid="merchant-business-name"
                    className="input-field mt-2"
                    placeholder="Your Company Name"
                    value={formData.business_name}
                    onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                  />
                </div>

                <div>
                  <Label htmlFor="contact_person" className="font-bold uppercase text-sm tracking-wide">Contact Person *</Label>
                  <Input
                    id="contact_person"
                    required
                    data-testid="merchant-contact-person"
                    className="input-field mt-2"
                    placeholder="Full Name"
                    value={formData.contact_person}
                    onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="email" className="font-bold uppercase text-sm tracking-wide">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      required
                      data-testid="merchant-email"
                      className="input-field mt-2"
                      placeholder="email@company.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="font-bold uppercase text-sm tracking-wide">Phone *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      required
                      data-testid="merchant-phone"
                      className="input-field mt-2"
                      placeholder="+234 XXX XXX XXXX"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="address" className="font-bold uppercase text-sm tracking-wide">Business Address *</Label>
                  <Textarea
                    id="address"
                    required
                    data-testid="merchant-address"
                    className="input-field mt-2"
                    placeholder="Full business address"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  />
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border-2 border-blue-200 p-4 rounded-sm">
              <h2 className="font-bold text-blue-800 mb-3">Products You Supply</h2>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="oil_brands" className="font-bold uppercase text-sm tracking-wide text-blue-700">Oil Brands</Label>
                  <Input
                    id="oil_brands"
                    data-testid="merchant-oil-brands"
                    className="input-field mt-2"
                    placeholder="Mobil, Shell, Total (comma separated)"
                    value={formData.oil_brands}
                    onChange={(e) => setFormData({ ...formData, oil_brands: e.target.value })}
                  />
                  <p className="text-xs text-blue-600 mt-1">Enter brand names separated by commas</p>
                </div>

                <div>
                  <Label htmlFor="filter_brands" className="font-bold uppercase text-sm tracking-wide text-blue-700">Filter Brands</Label>
                  <Input
                    id="filter_brands"
                    data-testid="merchant-filter-brands"
                    className="input-field mt-2"
                    placeholder="Bosch, Mann, Fram (comma separated)"
                    value={formData.filter_brands}
                    onChange={(e) => setFormData({ ...formData, filter_brands: e.target.value })}
                  />
                  <p className="text-xs text-blue-600 mt-1">Enter brand names separated by commas</p>
                </div>
              </div>
            </div>

            <div className="bg-green-50 border-2 border-green-200 p-4 rounded-sm">
              <h2 className="font-bold text-green-800 mb-3">Login Credentials</h2>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="password" className="font-bold uppercase text-sm tracking-wide text-green-700">Password *</Label>
                  <Input
                    id="password"
                    type="password"
                    required
                    data-testid="merchant-password"
                    className="input-field mt-2"
                    placeholder="Minimum 6 characters"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  />
                </div>

                <div>
                  <Label htmlFor="confirmPassword" className="font-bold uppercase text-sm tracking-wide text-green-700">Confirm Password *</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    required
                    data-testid="merchant-confirm-password"
                    className="input-field mt-2"
                    placeholder="Re-enter password"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  />
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 border-2 border-yellow-200 p-4 rounded-sm">
              <p className="text-sm text-yellow-800">
                <strong>Note:</strong> Your registration will be reviewed by our admin team. 
                You will be able to login once your account is approved.
              </p>
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="merchant-register-btn"
              className="btn-primary w-full h-12 text-lg"
            >
              {loading ? 'Submitting...' : 'Submit Registration'}
            </Button>
          </form>

          <p className="text-center mt-6 text-sm text-muted-foreground">
            Already registered?{' '}
            <Link to="/merchant/login" className="text-primary font-bold hover:underline">
              Login here
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
